/**
 * Break-glass mechanism that lets an authenticated admin bypass the
 * `requiredPermission` gate enforced by `RequireAuth` in case the BE returns
 * an incomplete / wrong list of permissions and the admin is locked out of a
 * page they should actually be able to use.
 *
 * Activation
 * ----------
 * Append `?bypassPermissions=true` to any route URL. Once detected, the flag
 * is persisted in `sessionStorage`, so subsequent navigations within the same
 * tab keep the bypass active without having to re-append the query string.
 * Closing the tab (or opening a new one) clears it.
 *
 * Important
 * ---------
 * The bypass only relaxes the *permission* check — `RequireAuth` still
 * enforces the auth gate (valid token / refresh token). It does NOT grant any
 * actual backend access; if the BE rejects the call for missing permissions
 * the page will simply render but its requests will fail. This is intentional:
 * the goal is to unblock the FE so the admin can at least navigate.
 *
 * Visibility
 * ----------
 * The first activation in a session emits a single `console.warn` so the
 * footprint is visible in the browser console (and easy to spot in support
 * sessions / screen shares) without spamming the log on every render.
 */

const SESSION_STORAGE_KEY = "pam_bypass_permissions";
const URL_QUERY_PARAM = "bypassPermissions";
const URL_QUERY_VALUE = "true";

let warnedThisSession = false;

/** Read the query param defensively; SSR / locked-down sandboxes lack `window`. */
function readBypassFromUrl(): boolean {
  if (typeof window === "undefined" || !window.location?.search) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(URL_QUERY_PARAM) === URL_QUERY_VALUE;
  } catch {
    return false;
  }
}

/** Read from sessionStorage defensively; private modes / SSR may throw. */
function readBypassFromSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage?.getItem(SESSION_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/** Persist the flag for the rest of the tab's lifetime. */
function persistBypass(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(SESSION_STORAGE_KEY, "true");
  } catch {
    // sessionStorage can be disabled (private browsing on some platforms,
    // strict CSP, etc.). Falling back to per-URL behaviour is fine.
  }
}

/** Single, deduplicated warning so the bypass is observable in devtools. */
function warnOnce(): void {
  if (warnedThisSession) return;
  warnedThisSession = true;
  // eslint-disable-next-line no-console -- intentional diagnostic for support flows.
  console.warn(
    "[PAM] Permission bypass active (?bypassPermissions=true). " +
      "RequireAuth is skipping the requiredPermission gate for this tab. " +
      "Close the tab to disable."
  );
}

/**
 * Returns whether the permission gate should be skipped for the current
 * render. Reads the URL first (so freshly added query params take effect
 * immediately) and falls back to a sessionStorage flag set by previous
 * activations.
 *
 * Side effect: when the URL form is detected for the first time the flag is
 * persisted in `sessionStorage` and a single `console.warn` is emitted.
 */
export function isPermissionBypassActive(): boolean {
  const fromUrl = readBypassFromUrl();
  if (fromUrl) {
    persistBypass();
    warnOnce();
    return true;
  }

  if (readBypassFromSession()) {
    warnOnce();
    return true;
  }

  return false;
}
